import ArrowBackLight from "@assets/navigation/back.svg";
import LightCustomButton from "@components/buttons/LightCustomButton";
import MobileTabSwitcher from "@components/switcher/MobileTabSwitcher";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  isPaysonicHost,
  isPrimeBrazilOnlyHost,
} from "src/config/deploymentConfig";
import ExpireNtxRampTradesSlider from "src/modules/Prime/components/ExpireNtxRampTradesSlider";
import NtxCircuitBreakerPanel from "src/modules/Prime/components/NtxCircuitBreakerPanel";
import ExtendNtxDeadlinesSlider from "src/modules/Prime/components/ExtendNtxDeadlinesSlider";
import BrNtxRatesSlider from "../../Prime/components/BrNtxRatesSlider";
import BrPixServiceStatus from "../../Prime/components/BrPixServiceStatus";
import InUpiServiceStatus from "../../Prime/components/InUpiServiceStatus";
import CadInternalTransferPanel from "../../Prime/components/CadInternalTransferPanel";
import CadManualPayoutPanel from "../../Prime/components/CadManualPayoutPanel";
import PrimeDetailsCanadaTabs from "../../Prime/PrimeDetailsCanadaTabs";
import PrimeDetailsEuropeTabs from "../../Prime/PrimeDetailsEuropeTabs";
import PrimeDetailsPhilippinesTabs from "../../Prime/PrimeDetailsPhilippinesTabs";
import PrimeDetailsTabs from "../../Prime/PrimeDetailsTabs";
import MobilePrimeOrderFlow from "./MobilePrimeOrderFlow";
import MobilePrimeOrderFlowCanada from "./MobilePrimeOrderFlowCanada";
import MobilePrimeOrderFlowEurope from "./MobilePrimeOrderFlowEurope";
import MobilePrimeOrderFlowPhilippines from "./MobilePrimeOrderFlowPhilippines";

const BRAZIL_TAB_INDEX = 1;
const INDIA_TAB_INDEX = 0;

type DetailView =
  | { type: "india" | "brazil"; tradeId: string; countryFilter: "IN" | "BR" }
  | { type: "europe"; instanceId: string }
  | { type: "philippines"; instanceId: string }
  | { type: "canada"; instanceId: string };

const MobilePrimeOrderFlowTabs = () => {
  const isPrimeBrazilOnly = isPrimeBrazilOnlyHost();
  const isPaysonic = isPaysonicHost();
  const navigate = useNavigate();
  const [detailView, setDetailView] = useState<DetailView | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isInternalTransferOpen, setIsInternalTransferOpen] = useState(false);
  const [isManualPayoutOpen, setIsManualPayoutOpen] = useState(false);
  const [isExtendPanelOpen, setIsExtendPanelOpen] = useState(false);
  const [isExpirePanelOpen, setIsExpirePanelOpen] = useState(false);
  const [isNtxMonitorOpen, setIsNtxMonitorOpen] = useState(false);
  const [isRatesPanelOpen, setIsRatesPanelOpen] = useState(false);
  const [ratesVersion, setRatesVersion] = useState(0);

  const handleShowDetail = (detail: DetailView) => {
    setDetailView(detail);
  };

  const allTabs = [
    {
      label: "India",
      component: (
        <MobilePrimeOrderFlow
          key="IN"
          countryFilter="IN"
          onShowDetail={(tradeId: string) =>
            handleShowDetail({ type: "india", tradeId, countryFilter: "IN" })
          }
        />
      ),
    },
    {
      label: "Brazil",
      component: (
        <MobilePrimeOrderFlow
          key="BR"
          countryFilter="BR"
          onShowDetail={(tradeId: string) =>
            handleShowDetail({ type: "brazil", tradeId, countryFilter: "BR" })
          }
        />
      ),
    },
    {
      label: "Europe",
      component: (
        <MobilePrimeOrderFlowEurope
          onShowDetail={(instanceId: string) =>
            handleShowDetail({ type: "europe", instanceId })
          }
        />
      ),
    },
    {
      label: "Philippines",
      component: (
        <MobilePrimeOrderFlowPhilippines
          onShowDetail={(instanceId: string) =>
            handleShowDetail({ type: "philippines", instanceId })
          }
        />
      ),
    },
    {
      label: "Canada",
      component: (
        <MobilePrimeOrderFlowCanada
          onShowDetail={(instanceId: string) =>
            handleShowDetail({ type: "canada", instanceId })
          }
        />
      ),
    },
  ];

  const tabs = isPrimeBrazilOnly
    ? allTabs.filter((tab) => tab.label === "Brazil")
    : isPaysonic
      ? allTabs.filter((tab) => tab.label === "India")
      : allTabs;

  const showBrPixStatus =
    !isPaysonic && (isPrimeBrazilOnly || activeTabIndex === BRAZIL_TAB_INDEX);

  const showPrimeIndiaVpnSettings =
    !isPrimeBrazilOnly && (isPaysonic || activeTabIndex === INDIA_TAB_INDEX);

  const showCadInternalTransfer =
    !isPrimeBrazilOnly &&
    !isPaysonic &&
    tabs[activeTabIndex]?.label === "Canada";

  if (detailView) {
    return (
      <div
        className="flex flex-col w-full"
        style={{ height: "calc(100dvh - 56px)" }}
      >
        <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row items-center px-4 shrink-0 gap-3">
          <img
            src={ArrowBackLight}
            alt="Back"
            className="cursor-pointer"
            onClick={() => setDetailView(null)}
          />
          <p className="text-text-base text-[14px] font-semibold">
            X RAMP Trade Details
          </p>
        </div>
        <div className="flex-1 overflow-auto p-3 min-h-0">
          {(detailView.type === "india" || detailView.type === "brazil") && (
            <PrimeDetailsTabs
              tradeId={detailView.tradeId}
              countryFilter={detailView.countryFilter}
            />
          )}
          {detailView.type === "europe" && (
            <PrimeDetailsEuropeTabs instanceId={detailView.instanceId} />
          )}
          {detailView.type === "philippines" && (
            <PrimeDetailsPhilippinesTabs instanceId={detailView.instanceId} />
          )}
          {detailView.type === "canada" && (
            <PrimeDetailsCanadaTabs instanceId={detailView.instanceId} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full"
      style={{ height: "calc(100dvh - 56px)" }}
    >
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 px-4 shrink-0">
        <div className="flex flex-row justify-between items-center w-full gap-2 min-w-0">
          <p className="text-text-base text-[16px] font-semibold shrink-0">
            Cross Ramp Order Flow
          </p>
          <div className="flex items-center gap-2 min-w-0 overflow-x-auto">
            {showPrimeIndiaVpnSettings && <InUpiServiceStatus />}
            {showPrimeIndiaVpnSettings && (
              <LightCustomButton
                text="VPN"
                isActive={true}
                onClick={() => navigate("/prime-order-flow/vpn-settings")}
                textSize="12px"
                className="shrink-0 min-w-[52px]"
              />
            )}
            {showCadInternalTransfer && (
              <LightCustomButton
                text="Internal Transfer"
                isActive={true}
                onClick={() => setIsInternalTransferOpen(true)}
                textSize="12px"
                className="shrink-0 min-w-[110px]"
              />
            )}
            {showCadInternalTransfer && (
              <LightCustomButton
                text="Manual Payout"
                isActive={true}
                onClick={() => setIsManualPayoutOpen(true)}
                textSize="12px"
                className="shrink-0 min-w-[110px]"
              />
            )}
            {showBrPixStatus && (
              <div className="min-w-0 overflow-x-auto">
                <BrPixServiceStatus
                  onOpenRates={() => setIsRatesPanelOpen(true)}
                  ratesVersion={ratesVersion}
                />
              </div>
            )}
            {showBrPixStatus && (
              <LightCustomButton
                text="EXT NTX"
                onClick={() => setIsExtendPanelOpen(true)}
                isActive={true}
                textSize="12px"
                className="min-w-[84px] shrink-0"
              />
            )}
            {showBrPixStatus && (
              <LightCustomButton
                text="EXP NTX"
                onClick={() => setIsExpirePanelOpen(true)}
                isActive={true}
                textSize="12px"
                className="min-w-[84px] shrink-0"
              />
            )}
            {showBrPixStatus && (
              <LightCustomButton
                text="NTX"
                onClick={() => setIsNtxMonitorOpen(true)}
                isActive={true}
                textSize="12px"
                className="min-w-[52px] shrink-0"
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 mt-3 px-3 flex flex-col">
        <MobileTabSwitcher tabs={tabs} onTabChange={setActiveTabIndex} />
      </div>

      <CadInternalTransferPanel
        isOpen={isInternalTransferOpen}
        onClose={() => setIsInternalTransferOpen(false)}
      />
      <CadManualPayoutPanel
        isOpen={isManualPayoutOpen}
        onClose={() => setIsManualPayoutOpen(false)}
      />
      <ExtendNtxDeadlinesSlider
        isOpen={isExtendPanelOpen}
        onClose={() => setIsExtendPanelOpen(false)}
      />
      <ExpireNtxRampTradesSlider
        isOpen={isExpirePanelOpen}
        onClose={() => setIsExpirePanelOpen(false)}
      />
      <NtxCircuitBreakerPanel
        isOpen={isNtxMonitorOpen}
        onClose={() => setIsNtxMonitorOpen(false)}
      />
      <BrNtxRatesSlider
        isOpen={isRatesPanelOpen}
        onClose={() => setIsRatesPanelOpen(false)}
        onSaved={() => setRatesVersion((version) => version + 1)}
      />
    </div>
  );
};

export default MobilePrimeOrderFlowTabs;
